import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Ion } from "@/components/Ion";
import { useTheme } from "@/lib/theme";

export const ImpersonationBanner = () => {
  const { impersonation, returnFromImpersonation } = useAuth();
  const [returning, setReturning] = useState(false);
  if (!impersonation) return null;
  const onReturn = async () => {
    if (returning) return;
    setReturning(true);
    try { await returnFromImpersonation(); } catch { setReturning(false); }
  };
  return (
    <div className="sticky top-0 z-[10050] border-b border-[#F5A623] bg-[#FFF3C4] pt-2" data-testid="impersonation-banner">
      <div className="h-[3px] bg-[#F5A623]" />
      <div className="flex items-center px-3 py-2">
        <Ion name="eye-outline" size={16} color="#0A0A0A" />
        <div className="ml-2 min-w-0 flex-1">
          <p className="text-[11px] font-black tracking-[1.2px] text-[#0A0A0A]" data-testid="impersonation-name">ADMIN SUPPORT · LOGGED IN AS {(impersonation.name || "").toUpperCase()}</p>
          <p className="mt-px truncate text-[11px] text-[#5A4A00]">You have full access to {impersonation.email}'s portal. Everything you do is logged. Session ends in 2 hours.</p>
        </div>
        <button onClick={onReturn} disabled={returning} data-testid="impersonation-return" className="ml-2 flex items-center gap-1 rounded-md bg-[#F5A623] px-2.5 py-[7px] disabled:opacity-60">
          {returning ? <Loader2 size={14} className="animate-spin" /> : <><Ion name="arrow-back" size={14} color="#0A0A0A" /><span className="ml-0.5 text-[11px] font-black tracking-[0.8px] text-[#0A0A0A]">RETURN TO ADMIN</span></>}
        </button>
      </div>
    </div>
  );
};

const exempt = (p) => p === "/" || p === "/insurance" || p.startsWith("/insurance/");

// Port of InsuranceGate.tsx — franchisee insurance redirects + soft banner.
export const InsuranceGate = ({ children }) => {
  const { user, loading } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const [status, setStatus] = useState(null);

  useEffect(() => {
    if (!user || user.role !== "franchisee") { setStatus(null); return; }
    api("/insurance/status").then((r) => setStatus({ ...r, _path: pathname })).catch(() => setStatus({ status: "ok", grace_days: 30, _path: pathname }));
  }, [user, pathname]);

  useEffect(() => {
    if (!status || status._path !== pathname || loading || !user || user.role !== "franchisee" || exempt(pathname)) return;
    const from = encodeURIComponent(pathname);
    if (["default", "locked"].includes(status.status)) navigate(`/insurance?locked=1&from=${from}`, { replace: true });
    else if (status.status === "needs_update") navigate(`/insurance?from=${from}`, { replace: true });
  }, [status, loading, user, pathname, navigate]);

  const show = user?.role === "franchisee" && ["expiring", "new_grace"].includes(status?.status) && !exempt(pathname);
  const grace = status?.status === "new_grace";
  const c = grace ? "#60A5FA" : "#FACC15";
  const n = grace ? status?.days_until_default ?? 0 : status?.days_remaining ?? 0;
  return (
    <>
      {show && (
        <button onClick={() => navigate("/insurance")} data-testid="insurance-soft-banner" className="flex w-full items-center gap-2.5 border-b px-4 py-2.5 text-left" style={{ borderColor: c, background: `${c}1A` }}>
          <Ion name="alert-circle" size={18} color={c} />
          <span className="flex-1">
            <span className="block text-[13px] font-extrabold tracking-[0.3px]" style={{ color: c }}>{grace ? `Upload insurance: ${n} day${n === 1 ? "" : "s"} left` : `Insurance expiring in ${n} day${n === 1 ? "" : "s"}`}</span>
            <span className="mt-px block text-xs" style={{ color: theme.text }}>{grace ? "Tap to upload your current policy before your account is suspended." : "Tap to upload your renewed policy."}</span>
          </span>
          <Ion name="chevron-forward" size={18} color={theme.textSecondary} />
        </button>
      )}
      {children}
    </>
  );
};

const bypassKey = (id) => `lso_banner_admin_bypass:${id}`;
const plural = (n) => (n === 1 ? "" : "s");

function lsoCopy(s) {
  const urgent = ["past_due", "locked", "renewal_overdue"].includes(s.status);
  const warning = s.status === "renewal_due" || (s.status === "active" && (s.days_remaining ?? 999) <= 15) || (s.status === "certified" && (s.days_until_cert_expiry ?? 999) <= 30);
  const fresh = s.status === "active" && (s.days_remaining ?? 999) > 15;
  if (!urgent && !warning && !fresh) return null;
  let title = "Complete your updated LSO Training";
  let sub = "";
  if (s.status === "active") { const d = s.days_remaining ?? 0; sub = `${d} day${plural(d)} remaining to complete the 10-module ALR LSO course.`; }
  else if (s.status === "past_due") {
    const over = Math.abs(s.days_remaining ?? 0), lockIn = Math.max(0, 45 - (s.days_since_start ?? 0));
    title = `LSO Training is PAST DUE — ${over} day${plural(over)} overdue`;
    sub = `Account locks in ${lockIn} day${plural(lockIn)}. Complete the LSO course now.`;
  } else if (s.status === "locked" && s.self_unlock) { title = "Your account is LOCKED — finish LSO Training to unlock"; sub = "Pass the course and your account unlocks automatically. Tap to continue."; }
  else if (s.status === "locked" || s.status === "renewal_overdue") { title = "Your account is LOCKED — admin reset required"; sub = s.lock_reason || "Contact ALR corporate to restore access."; }
  else if (s.status === "renewal_due") { const d = s.days_remaining ?? 0; title = "LSO certificate expired — renew now"; sub = `${d} day${plural(d)} remaining in renewal window.`; }
  else if (s.status === "certified") { const d = s.days_until_cert_expiry; title = "LSO certificate expires soon"; sub = `${d} day${plural(d)} until expiry — renew to avoid the 30-day renewal countdown.`; }
  return { urgent, warning, title, sub, label: urgent ? "URGENT" : warning ? "ACTION REQUIRED" : "NEW LSO TRAINING" };
}

export const LsoTrainingBanner = () => {
  const { user, impersonation } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [bypass, setBypass] = useState(false);
  const bannerRef = useRef(null);
  useEffect(() => {
    const el = bannerRef.current;
    const root = document.documentElement.style;
    if (!el) { root.setProperty("--lso-banner-h", "0px"); return undefined; }
    const ro = new ResizeObserver(() => root.setProperty("--lso-banner-h", `${el.offsetHeight}px`));
    ro.observe(el);
    return () => { ro.disconnect(); root.setProperty("--lso-banner-h", "0px"); };
  });

  useEffect(() => { setBypass(user?.role === "admin" && localStorage.getItem(bypassKey(user.id)) === "true"); }, [user]);
  useEffect(() => {
    if (!user || !["franchisee", "franchisee_employee"].includes(user.role) || user.is_demo_persona) return;
    api("/lso/status/me").then(setStatus).catch(() => {});
  }, [user, pathname]);

  if (!user || !["franchisee", "admin"].includes(user.role) || (user.role === "admin" && bypass) || !status) return null;
  if (pathname.startsWith("/lso") || impersonation) return null;
  if (status.status === "certified" && !status.locked && (status.days_until_cert_expiry ?? 999) > 30) return null;
  const c = lsoCopy(status);
  if (!c) return null;
  const bg = !c.urgent && c.warning ? "#b45309" : "#b91c1c";
  const border = !c.urgent && c.warning ? "#f59e0b" : "#ef4444";
  return (
    <div ref={bannerRef} className="fixed inset-x-0 bottom-0 z-[9998] min-[900px]:left-[264px] border-t-2 px-3.5 pb-3 pt-2.5" style={{ background: bg, borderTopColor: border }} data-testid="lso-training-banner">
      <button className="flex w-full items-center text-left" onClick={() => navigate("/lso")} data-testid="lso-banner-open">
        <Ion name={c.urgent ? "warning" : "school"} size={22} color="#fff" style={{ marginRight: 10 }} />
        <span className="flex-1 text-white">
          <span className="mb-0.5 block text-[9px] font-extrabold tracking-[1.6px] opacity-95">{c.label}</span>
          <span className="block text-[13px] font-extrabold">{c.title}</span>
          {c.sub && <span className="mt-0.5 block text-[11px] font-medium opacity-90">{c.sub}</span>}
        </span>
        <span className="ml-2 flex items-center rounded-md bg-white/20 px-2.5 py-1.5 text-xs font-extrabold text-white">Open<Ion name="chevron-forward" size={16} color="#fff" /></span>
      </button>
      {user.role === "admin" && (
        <button aria-label="Dismiss LSO Training banner (admin only)" data-testid="lso-banner-admin-dismiss" onClick={() => { localStorage.setItem(bypassKey(user.id), "true"); setBypass(true); }}
          className="absolute right-1.5 top-1 flex h-[22px] w-[22px] items-center justify-center rounded-full bg-white/20"><Ion name="close" size={14} color="#fff" /></button>
      )}
    </div>
  );
};
